import { unzipSync } from 'fflate';

/**
 * Extracts a KML string (and any embedded file assets) from either a plain KML blob or
 * a KMZ (ZIP) blob. KMZ archives contain "doc.kml" plus an optional "files/" directory
 * with icon images and other resources referenced by relative href paths.
 *
 * Data URLs (base64-encoded) are used instead of blob: URLs because the Google Maps Data
 * Layer does not reliably load blob: URLs as marker icon sources.
 */
export function extractKmlText(
  blob: Blob,
  fileName: string
): Promise<{ kmlText: string; embeddedFiles: Map<string, string> }> {
  const isKmz = fileName.toLowerCase().endsWith('.kmz');
  if (!isKmz) return blob.text().then((kmlText) => ({ kmlText, embeddedFiles: new Map() }));

  return blob.arrayBuffer().then((buf) => {
    // unzipSync is synchronous after the blob read — no async callback gap,
    // which prevents a second fitBounds animation after Google Maps' initial one.
    const files = unzipSync(new Uint8Array(buf));
    const kmlEntry = files['doc.kml'] ?? Object.entries(files).find(([k]) => k.endsWith('.kml'))?.[1];
    if (!kmlEntry) throw new Error('No .kml file found inside KMZ archive');
    const kmlText = new TextDecoder().decode(kmlEntry);

    const embeddedFiles = new Map<string, string>();
    for (const [path, data] of Object.entries(files)) {
      const lowerPath = path.toLowerCase();
      if (lowerPath.endsWith('.kml')) continue;
      const mimeType = lowerPath.endsWith('.png')
        ? 'image/png'
        : lowerPath.endsWith('.jpg') || lowerPath.endsWith('.jpeg')
          ? 'image/jpeg'
          : lowerPath.endsWith('.gif')
            ? 'image/gif'
            : 'application/octet-stream';
      // Convert Uint8Array to base64 in chunks to avoid call-stack overflow on large files.
      let binary = '';
      const chunk = 8192;
      for (let i = 0; i < data.byteLength; i += chunk) {
        binary += String.fromCharCode(...data.subarray(i, i + chunk));
      }
      embeddedFiles.set(path, `data:${mimeType};base64,${btoa(binary)}`);
    }

    return { kmlText, embeddedFiles };
  });
}
